import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import { getAPIConfig, setSessionStorage } from '@tcp/core/src/utils';
import withStyles from '../../../../../../common/hoc/withStyles';
import styles from '../styles/CategoryPills.style';
import { getLabelFromCopyOrFromTranslation } from '../../../../../../../utils/labels/labelUtilty';

const CategoryPillLabel = {
  PLP: {
    plpTiles: {
      lbl_plp_browse_more: 'Browse More in',
    },
  },
};

/* eslint-disable sonarjs/cognitive-complexity */
const CategoryPills = (props) => {
  const { className, navigation, level, pathName, onClick } = props;
  const router = useRouter();
  const [pillCategories, setPillCategories] = useState([]);
  const [l3PillCategories, setL3PillCategories] = useState([]);
  const [currentRouterPath, setCurrentRouterPath] = useState(router.asPath);
  const prevPillCategories = useRef();
  const prevL3PillCategories = useRef();
  const prevRouterPath = useRef();
  const apiConfigObj = getAPIConfig();
  const { country } = apiConfigObj || {};
  const currentLevel = level?.length || 0;
  const [pathWithoutQuery] = pathName?.split('?') || [''];

  useEffect(() => {
    const [routeWithoutQuery] = router.asPath.split('?');
    if (currentRouterPath !== routeWithoutQuery) {
      setPillCategories([]);
      prevPillCategories.current = null;
    }

    setCurrentRouterPath(routeWithoutQuery);
    prevRouterPath.current = routeWithoutQuery;
  }, [router]);

  useEffect(() => {
    const subCategories = navigation?.subCategories || {};
    // convert subCategories to array and sort by the order property
    const sortedSubArray = Object.entries(subCategories).sort((a, b) => a[1].order - b[1].order);
    // convert array back into object and loop through it
    const sortedSubCategories = Object.fromEntries(sortedSubArray);
    const catKeys = Object.keys(sortedSubCategories || {});

    catKeys.forEach((key) => {
      sortedSubCategories[key]?.items?.forEach((item) => {
        if (item?.categoryContent?.name === 'Shop All') {
          /* eslint-disable no-param-reassign */
          item.categoryContent.shopAllName = key;
          sortedSubCategories[key].hasShopAll = true;
          setPillCategories((prevState) => {
            if (
              (isEqual(prevState, item) || isEqual(prevState, prevPillCategories.current)) &&
              pillCategories.length
            ) {
              return prevState;
            }
            return [...prevState, item];
          });
        }
      });

      if (!sortedSubCategories[key]?.hasShopAll) {
        setPillCategories((prevState) => {
          if (
            (isEqual(prevState, ...sortedSubCategories[key]?.items) ||
              isEqual(prevState, prevPillCategories.current)) &&
            pillCategories.length
          ) {
            return prevState;
          }
          return [...prevState, ...sortedSubCategories[key]?.items];
        });
      }

      setL3PillCategories((prevState) => {
        if (
          isEqual(prevState, ...sortedSubCategories[key]?.items) ||
          isEqual(prevState, prevL3PillCategories.current)
        ) {
          return prevState;
        }
        return [...prevState, ...sortedSubCategories[key]?.items];
      });
    });
  }, [navigation]);

  useEffect(() => {
    prevPillCategories.current = pillCategories;
  }, [pillCategories]);

  const L1Categories = pillCategories.filter(
    (menuItem) => menuItem?.categoryContent?.displayToCustomer
  );

  const L3FilteredCategories = l3PillCategories.filter(
    (menuItem) => menuItem?.categoryContent?.displayToCustomer
  );

  const pathNameWithoutCountry = pathWithoutQuery.replace(`/${country?.toLowerCase()}`, '');
  const L3MenuItems = L3FilteredCategories.filter((menuItem) => menuItem?.hasL3);
  const L2Categories = L3MenuItems.filter((element) => {
    const [elementPathWithoutQuery] = element?.asPath?.split('?') || [''];
    return elementPathWithoutQuery === pathNameWithoutCountry;
  });
  const L3Categories = L3MenuItems.filter(
    (element) =>
      element?.subCategories?.filter(
        (subCategory) => subCategory?.asPath === pathNameWithoutCountry
      ).length
  );

  const getCategoriesToMap = () => {
    if (currentLevel === 1) return L1Categories;
    if (currentLevel === 2) {
      if (L2Categories.length) {
        return L2Categories[0]?.subCategories?.filter(
          (subCategory) => subCategory?.categoryContent?.displayToCustomer
        );
      }
      return L1Categories;
    }
    return L3Categories[0]?.subCategories?.filter(
      (subCategory) => subCategory?.categoryContent?.displayToCustomer
    );
  };

  const getCurrentCategory = () => {
    if (currentLevel === 1) return navigation?.categoryContent?.name;
    if (currentLevel === 2) {
      if (L2Categories.length) {
        return L2Categories[0]?.categoryContent?.name;
      }
      return navigation?.categoryContent?.name;
    }
    return L3Categories[0]?.categoryContent?.name;
  };

  return getCategoriesToMap()?.length ? (
    <div className={className}>
      <div className="category-pill-container">
        <h4 className="category-pill-heading">
          {getLabelFromCopyOrFromTranslation('PLP.plpTiles.lbl_plp_browse_more', CategoryPillLabel)}{' '}
          {getCurrentCategory()}
        </h4>
        <ul className="category-pill-list">
          {getCategoriesToMap()?.map(({ asPath, categoryContent, url }, index) => (
            <li key={`${categoryContent?.catgroupId}-${index}`} className="category-pill">
              <Anchor
                className="category-pill-link"
                anchorVariation="secondary"
                to={`${url}`}
                dataLocator="category-pill-anchor-link"
                asPath={`${asPath}`}
                onClick={() => {
                  setSessionStorage({ key: 'categoryPillClicked', value: 1 });
                  if (onClick) {
                    onClick({
                      eventName: 'Navigation Clicks [e82]',
                      customEvents: ['event82'],
                      categoryName: `categorypill-${getCurrentCategory()}`,
                    });
                  }
                }}
              >
                {categoryContent?.shopAllName
                  ? categoryContent?.shopAllName
                  : categoryContent?.name}
              </Anchor>
            </li>
          ))}
        </ul>
      </div>
    </div>
  ) : null;
};

CategoryPills.propTypes = {
  className: PropTypes.string,
  navigation: PropTypes.shape({
    subCategories: PropTypes.object,
    categoryContent: PropTypes.shape({
      name: PropTypes.string,
    }),
  }),
  level: PropTypes.arrayOf(PropTypes.number),
  pathName: PropTypes.string,
  onClick: PropTypes.func,
};

export default React.memo(withStyles(CategoryPills, styles));
